/**
 * Vietnamesische Texte der Einstellungsseite.
 *
 * Aufbau und Reihenfolge folgen roboter-einstellungen.ts; Aufrufe, Zeitpunkte
 * und Quellen kommen von dort. roboter-einstellungen-sprachen.test.ts prueft,
 * dass beides zusammenpasst.
 */

import type { Texte } from "./roboter-einstellungen-sprachen";

const vi: Texte = {
  zeitpunkt: {
    jederzeit: "bất cứ lúc nào, kể cả khi đang chạy",
    stillstand: "chỉ khi đứng yên – nếu không sẽ báo RobotBusyError",
    "vor-dem-start": "một lần, ở đầu chương trình",
    blockiert: "chặn chương trình cho đến khi xong",
  },
  grundsaetze: [
    "Thiết lập có hiệu lực từ lúc gọi đến khi chương trình kết thúc. Sau mỗi lần khởi động – kể cả sau khi gửi chương trình và sau khi bật nguồn – các giá trị mặc định lại có hiệu lực. Vì vậy chúng thuộc về chương trình, ngay sau robot.init().",
    "Nếu một giá trị bị bỏ qua khi gọi, nó giữ nguyên. drive.configure(max_speed_m_s=0.6) chỉ thay đổi tốc độ tối đa.",
    "Mọi giá trị đều theo đơn vị SI: mét, giây, radian. Phần đuôi của tên cho biết: _m_s là m/s, _rad_s là rad/s, _s là giây.",
    "“Chỉ khi đứng yên” nghĩa là: chừng nào một lệnh di chuyển hoặc lệnh động cơ còn chiếm bộ truyền động, lời gọi trả lời bằng RobotBusyError. Hãy gọi robot.stop() hoặc kết thúc lệnh trước.",
    "Giá trị không hợp lệ không bị âm thầm điều chỉnh mà bị từ chối bằng ValueError. Thông báo lỗi nêu phạm vi cho phép.",
  ],
  gruppen: {
    start: {
      titel: "Khởi động và bảng gán chân",
      einleitung:
        "robot.init() kiểm tra dây nối, khởi động phần cứng và đo ắc quy. Không có lời gọi này, mọi thiết lập khác đều không có tác dụng. Động cơ vẫn tắt.",
      einstellungen: [
        {
          name: "Bảng gán chân",
          vorgabe: "không có – bắt buộc phải khai báo",
          bereich:
            "GPIO 0–22 và 26–28. battery_adc chỉ 26, 27 hoặc 28 (đầu vào tương tự). imu.int có thể bỏ qua; firmware không dùng chân này, nó chỉ được ghi nhận là đã dùng. Mỗi chân tối đa một lần.",
          wirkung:
            "Xác định động cơ, encoder, cảm biến tư thế, cảm biến siêu âm, nút bấm, LED và mạch đo ắc quy nối với những chân nào của Pico.",
          hinweis:
            "GPIO 23, 24, 25 và 29 thuộc về mô-đun vô tuyến và nguồn cấp của bo mạch nên bị khóa. Một khai báo thiếu, trùng hoặc không hợp lệ sẽ dừng với PinError – và thông báo nêu rõ vị trí.",
        },
      ],
      fest: [
        {
          name: "Đo ắc quy khi khởi động",
          wert: "25 lần đo, cách nhau 2 ms",
          bedeutung:
            "robot.init() chặn chương trình khoảng 50 ms cho việc này. Nếu đo thất bại, init dừng với RobotFaultError – không có điện áp hợp lệ thì động cơ vẫn bị khóa.",
        },
      ],
    },
    fahrwerk: {
      titel: "Khung xe",
      einleitung:
        "Giới hạn và hành vi cho drive.velocity(), drive.straight(), drive.turn() và mọi lệnh di chuyển khác. Nếu một lệnh vượt quá giới hạn, robot từ chối nó bằng ValueError – nó không đơn giản là chạy chậm hơn.",
      einstellungen: [
        {
          name: "Tốc độ tối đa",
          vorgabe: "0,4 m/s",
          bereich: "lớn hơn 0",
          wirkung:
            "Lệnh di chuyển với tốc độ cao hơn bị từ chối: drive.velocity(0.5, 0) phát ra ValueError và kết thúc chương trình nếu chương trình không bắt lỗi.",
          hinweis:
            "0,4 m/s là giá trị tạm và chưa bao giờ được xác định trên robot. Nó nhằm ngăn một lỗi gõ như drive.velocity(4, 0) làm robot lao đi. Chú ý với joystick: y * 0.5 cho 0,5 m/s khi đẩy hết cần, cao hơn giới hạn.",
        },
        {
          name: "Tốc độ quay tối đa",
          vorgabe: "3,0 rad/s (khoảng 172 °/s)",
          bereich: "lớn hơn 0",
          wirkung: "Lệnh quay vượt tốc độ này bị từ chối bằng ValueError.",
          hinweis: "Giống tốc độ tối đa, đây là giá trị tạm chưa bao giờ được xác định.",
        },
        {
          name: "Gia tốc",
          vorgabe: "không có dốc tăng – giá trị đặt nhảy thẳng",
          bereich: "lớn hơn 0",
          wirkung:
            "Giới hạn tốc độ đặt được phép thay đổi nhanh đến đâu. Robot xuất phát và phanh êm hơn.",
          hinweis:
            "Không có dốc tăng, robot bị giật khi xuất phát, và một cấu hình giữ thăng bằng có thể bị đổ.",
        },
        {
          name: "Hành vi khi kết thúc tác vụ di chuyển",
          vorgabe: '"coast" – quay trơn đến khi dừng',
          bereich: '"coast" quay trơn, "brake" phanh, "hold" giữ vị trí',
          wirkung:
            "Động cơ làm gì khi drive.straight() hoặc drive.turn() đã đạt mục tiêu.",
          hinweis:
            "\"hold\" tiếp tục điều khiển và tốn điện. Để dừng toàn bộ hãy dùng robot.stop(), không phải thiết lập này.",
        },
        {
          name: "Giới hạn thời gian cho tác vụ di chuyển",
          vorgabe: "20 s",
          bereich: "lớn hơn 0",
          wirkung:
            "Một tác vụ có mục tiêu – quãng đường, góc, thời gian – không đạt mục tiêu trong thời gian này sẽ bị hủy và báo lại.",
          hinweis:
            "Không áp dụng cho các lệnh liên tục như drive.velocity() hay motor_left.run(): chúng chạy cho đến khi có lệnh khác.",
        },
      ],
      fest: [
        {
          name: "Đường kính bánh xe",
          wert: "90 mm",
          bedeutung:
            "Đổi số vòng quay bánh xe thành mét. Giá trị tạm, còn phải xác nhận trên robot – nếu sai thì quãng đường và tốc độ cũng sai.",
        },
        {
          name: "Khoảng cách hai bánh",
          wert: "110 mm",
          bedeutung:
            "Khoảng cách giữa các bánh xe; quyết định robot quay được bao nhiêu khi quay. Giá trị tạm, còn phải xác nhận trên robot.",
        },
      ],
    },
    motoren: {
      titel: "Động cơ và bộ điều khiển tốc độ",
      einleitung:
        "Mỗi động cơ có hai bộ điều khiển: một cho tốc độ quay (motor_left.run(), mọi lệnh di chuyển) và một cho góc (motor_left.run_to_angle(), motor_left.run_for_angle()). Thiết lập áp dụng cho từng động cơ – với cả hai bên thì gọi hai lần.",
      einstellungen: [
        {
          name: "Bộ điều khiển tốc độ",
          vorgabe: "kp 6,0 · ki 12,0 · kd 0,0",
          bereich: 'loop "speed" hoặc "position"; hệ số là số bất kỳ',
          wirkung:
            "Bộ điều khiển phản ứng mạnh đến đâu với sai lệch tốc độ. kp tức thì, ki theo thời gian, kd theo sự thay đổi.",
          hinweis:
            "Giá trị mặc định là giá trị tạm; các bộ điều khiển chưa được chỉnh. Trên robot, kp = 8 và ki = 40 đã cho kết quả tốt. Thay đổi trong khi chạy được phép rõ ràng – phần I đã tích lũy được giữ lại để tốc độ không bị nhảy. Kết hợp tốt với thanh trượt từ remote.number().",
        },
        {
          name: "Bộ điều khiển góc",
          vorgabe: "kp 4,0 · ki 0,0 · kd 0,2",
          wirkung: "Động cơ tiến đến và giữ một góc mục tiêu như thế nào.",
        },
        {
          name: "Độ rộng xung tối đa",
          vorgabe: "100 %",
          bereich: "trên 0 đến 100",
          wirkung: "Giới hạn mức điều khiển động cơ theo phần trăm.",
          hinweis:
            "Độc lập với điều này, giới hạn 6 V luôn có hiệu lực (xem An toàn): khi ắc quy đầy, 100 % ở đây không bao giờ là 100 % ở động cơ.",
        },
        {
          name: "Gia tốc",
          vorgabe: "không có dốc tăng",
          bereich: "lớn hơn 0",
          wirkung: "Giới hạn tốc độ đặt được phép thay đổi nhanh đến đâu, tính bằng rad/s².",
        },
        {
          name: "Hành vi khi kết thúc tác vụ",
          vorgabe: '"coast" – quay trơn đến khi dừng',
          bereich: '"coast", "brake", "hold"',
          wirkung: "Động cơ làm gì khi một tác vụ có mục tiêu đã xong.",
        },
        {
          name: "Giới hạn thời gian cho tác vụ",
          vorgabe: "20 s",
          bereich: "lớn hơn 0",
          wirkung: "Hủy một tác vụ có mục tiêu nếu không đạt được trong thời gian này.",
          hinweis: "Không áp dụng cho motor_left.run() – lệnh liên tục không có mục tiêu.",
        },
        {
          name: "Phát hiện kẹt",
          vorgabe: "0,5 s",
          bereich: "lớn hơn 0",
          wirkung:
            "Nếu bánh xe đứng yên lâu như vậy dù bộ điều khiển yêu cầu ít nhất 25 % độ rộng xung, bộ truyền động được coi là bị kẹt: bộ điều khiển tắt, tác vụ kết thúc, và động cơ dừng theo stop_mode của nó.",
          hinweis:
            "1,5 s đầu sau khi xuất phát không được kiểm tra – nếu không, việc xuất phát chống lại ma sát tĩnh trông giống hệt bị kẹt. Với hệ số điều khiển rất yếu, động cơ có thể bị coi nhầm là bị kẹt.",
        },
      ],
      fest: [
        {
          name: "Tần số PWM",
          wert: "500 Hz",
          bedeutung: "Tần số dùng để điều khiển động cơ.",
        },
      ],
    },
    regler: {
      titel: "Bộ điều khiển riêng",
      einleitung:
        "control.start() chạy một hàm riêng trên lõi điều khiển, theo nhịp cố định và độc lập với chương trình. control.PID là bộ điều khiển có sẵn với giới hạn và chống bão hòa tích phân.",
      einstellungen: [
        {
          name: "Chu kỳ điều khiển",
          vorgabe: "không có – bắt buộc phải khai báo",
          bereich: "0,002 s đến 1,0 s",
          wirkung: "Hàm riêng được gọi thường xuyên đến đâu.",
          hinweis:
            "Không thể nhanh hơn nhịp cơ bản 2 ms. control.timing() cho biết nhịp có được giữ không – hãy xem ở đó trước khi một bộ điều khiển hoạt động lạ.",
        },
        {
          name: "Thời gian chạy cho phép mỗi lần gọi",
          vorgabe: "một nửa chu kỳ",
          bereich: "trên 0 đến period_s",
          wirkung: "Nếu một lần gọi lâu hơn, nó được tính là lỡ hạn.",
        },
        {
          name: "Bộ điều khiển PID",
          vorgabe: "không có – hệ số khi tạo",
          bereich: "limits dạng (dưới, trên); reverse=True đảo dấu",
          wirkung:
            "Tính giá trị đầu ra bằng pid.update(soll, ist, dt_s). pid.kp, pid.ki và pid.kd có thể thay đổi trực tiếp bất cứ lúc nào.",
        },
      ],
    },
    lage: {
      titel: "Cảm biến tư thế (IMU)",
      einleitung:
        "Cảm biến tư thế đo gia tốc và tốc độ quay. Một bộ lọc tính ra độ nghiêng từ đó – và với Mahony hoặc Madgwick là toàn bộ tư thế trong không gian.",
      einstellungen: [
        {
          name: "Xác định điểm không",
          vorgabe: "thời gian đo 3,0 s",
          bereich: "duration_s lớn hơn 0",
          wirkung:
            "Đo tốc độ quay khi đứng yên và từ đó trừ đi. Không hiệu chuẩn, các góc sẽ từ từ bị trôi.",
          hinweis:
            "Robot phải đứng yên. Nếu nó chuyển động, phép đo dừng với RobotFaultError. Đo trên thiết bị: không hiệu chuẩn, bộ lọc bù và Mahony cho độ lệch 0,26° khi đứng yên, sau khi hiệu chuẩn là 0,06°.",
        },
        {
          name: "Chọn bộ lọc",
          vorgabe: '"complementary"',
          bereich: '"complementary", "mahony", "madgwick"',
          wirkung:
            "Bộ lọc bù: chỉ độ nghiêng, rẻ nhất. Mahony và Madgwick: toàn bộ tư thế dưới dạng quaternion – chỉ khi đó imu.quaternion(), imu.orientation() và telemetry.send_orientation() mới trả về giá trị.",
          hinweis:
            "Đổi bộ lọc sẽ đặt lại độ nghiêng; imu.tilt() là None trong một bước đo. Thời gian tính mỗi lần đọc trên thiết bị: Mahony 230 µs, Madgwick 301 µs.",
        },
        {
          name: "Hằng số thời gian (bộ lọc bù)",
          vorgabe: "0,5 s",
          bereich: "lớn hơn 0",
          wirkung:
            "Dưới mức này tốc độ quay chiếm ưu thế, trên mức này là gia tốc. Nhỏ: bám nhanh nhưng dao động. Lớn: êm nhưng chậm chạp.",
        },
        {
          name: "Trọng số tốc độ quay (bộ lọc bù)",
          vorgabe: "tính từ hằng số thời gian",
          bereich: "0 đến 1",
          wirkung:
            "Đặt trọng số trực tiếp thay vì qua hằng số thời gian. 0 nghĩa là chỉ gia tốc, 1 là chỉ tốc độ quay – khi đó góc trôi đi không kiểm soát.",
          hinweis: "Một hằng số thời gian mới sẽ đặt lại gain đã đặt trước đó.",
        },
        {
          name: "Hiệu chỉnh kp và ki (Mahony)",
          vorgabe: "kp 2,0 · ki 0,0",
          bereich: "không âm",
          wirkung:
            "kp: gia tốc hiệu chỉnh tư thế mạnh đến đâu; 1/kp là hằng số thời gian. ki: ước lượng thêm điểm không của tốc độ quay trong khi chạy, 0 tắt tính năng này.",
        },
        {
          name: "Bước beta (Madgwick)",
          vorgabe: "0,1",
          bereich: "không âm",
          wirkung:
            "Madgwick hiệu chỉnh mọi sai số với tối đa khoảng 2 · beta rad/s, bất kể nó lớn đến đâu – không có hằng số thời gian như Mahony.",
        },
        {
          name: "Bộ lọc thông thấp trong cảm biến",
          vorgabe: "44 Hz",
          bereich: "0 (tắt), 5, 10, 21, 44, 94 hoặc 184",
          wirkung:
            "Làm mịn gia tốc và tốc độ quay ngay trong cảm biến. Thấp hơn: êm hơn, nhưng trễ hơn.",
          hinweis:
            "Giá trị mới không được ghi ngay mà do lõi điều khiển ghi ở nhịp tiếp theo – ghi từ chương trình đã làm lõi điều khiển dừng đến 51 ms theo đo đạc.",
        },
        {
          name: "Hiệu chuẩn và góc tham chiếu bằng tay",
          vorgabe: "không có điểm không, thang đo 1, tham chiếu 0",
          bereich: "gyro_offset (rad/s), accel_offset (m/s²), accel_scale (hệ số), mỗi loại ba giá trị",
          wirkung:
            "Đặt điểm không và thang đo mà không cần đo, ví dụ giá trị đã lưu từ lần hiệu chuẩn trước. tilt_reference_rad là góc mà imu.tilt() xuất ra là 0.",
        },
      ],
      fest: [
        {
          name: "Nhịp đọc",
          wert: "mỗi 4 ms (250 Hz)",
          bedeutung: "Lõi điều khiển đọc cảm biến và tính bộ lọc với tần suất này.",
        },
      ],
    },
    odometrie: {
      titel: "Odometry",
      einleitung:
        "Odometry tính từ số vòng quay bánh xe xem robot đang ở đâu và hướng về đâu.",
      einstellungen: [
        {
          name: "Đặt vị trí",
          vorgabe: "(0, 0, 0) khi khởi động",
          wirkung: "Xác định robot hiện đang ở đâu và chỉ về hướng nào.",
          hinweis:
            "Vị trí trôi dần theo thời gian, nhất là hướng: trượt bánh và các giá trị tạm của đường kính bánh và khoảng cách hai bánh cộng dồn lại.",
        },
      ],
      fest: [
        {
          name: "Encoder",
          wert: "1440 xung mỗi vòng quay bánh",
          bedeutung: "Độ phân giải của phép đo quãng đường: khoảng 0,2 mm mỗi xung.",
        },
      ],
    },
    telemetrie: {
      titel: "Telemetry",
      einleitung:
        "Giá trị đo đến giao diện: dưới dạng đường cong, để ghi lại thành CSV và dưới dạng tư thế trong không gian.",
      einstellungen: [
        {
          name: "Gửi định kỳ bộ dữ liệu chuẩn",
          vorgabe: "tắt; khi bật là 0,1 s",
          bereich: "lớn hơn 0",
          wirkung:
            "Gửi định kỳ ắc quy, độ nghiêng, khoảng cách, tốc độ bánh xe và vị trí. telemetry.stop() kết thúc việc này.",
          hinweis:
            "Không bao giờ gửi quá 50 dòng mỗi giây; chu kỳ ngắn hơn chỉ gom giá trị lại. Mỗi dòng tốn của robot gần 1 ms thời gian tính.",
        },
        {
          name: "Hiển thị tư thế",
          vorgabe: "đường cong và hình 3D",
          bereich: "plot và view mỗi cái True hoặc False",
          wirkung:
            "plot hiển thị bốn giá trị thành đường cong, view hiển thị robot trong không gian. Cả hai False: chỉ nằm trong bản ghi.",
          hinweis:
            "Cần mode=\"mahony\" hoặc \"madgwick\". Với bộ lọc bù nó trả về False và không gửi gì – khi đó hình hiển thị vẫn trống.",
        },
      ],
    },
    rueckkanal: {
      titel: "Phần tử điều khiển từ giao diện",
      einleitung:
        "remote.number(), remote.switch(), remote.button() và remote.joystick() đăng ký phần tử điều khiển trong giao diện và trả về giá trị của chúng.",
      einstellungen: [
        {
          name: "Thời hạn khi mất kết nối",
          vorgabe: "2,0 s",
          bereich: "lớn hơn 0, hoặc None để tắt",
          wirkung:
            "Nếu giao diện không gửi tín hiệu trong khoảng thời gian này, mọi phần tử điều khiển trở về giá trị mặc định: joystick về vị trí nghỉ, thanh trượt và công tắc về giá trị ban đầu, các lần nhấn nút đang chờ bị hủy.",
          hinweis:
            "Thời hạn không thay thế lệnh dừng. Robot có dừng hay không là do chương trình chuyển tiếp giá trị joystick quyết định. remote.timeout() không có tham số trả về giá trị đang đặt.",
        },
      ],
      fest: [
        {
          name: "Lấy giá trị",
          wert: "tối đa mỗi 10 ms, trong robot.sleep()",
          bedeutung:
            "Giá trị mới chỉ đến trong robot.sleep(). Một vòng lặp với robot.sleep(0.05) vì thế chỉ đọc tối đa 20 lần mỗi giây.",
        },
      ],
    },
    funk: {
      titel: "Bluetooth",
      einleitung:
        "Qua Bluetooth, giao diện kết nối không cần cáp: gửi chương trình, console, telemetry, phần tử điều khiển.",
      einstellungen: [
        {
          name: "Bật sóng vô tuyến",
          vorgabe: "tắt",
          bereich: "tên dài 1 đến 29 ký tự",
          wirkung:
            "Robot quảng bá với tên này và có thể kết nối từ giao diện. Từ đó mọi dữ liệu xuất ra cũng đi qua sóng vô tuyến. ble.stop() tắt nó.",
          hinweis:
            "Không có ble.start() trong chương trình, sau khi gửi chương trình robot không kết nối lại được qua sóng vô tuyến – mỗi lần khởi động lại đều tắt sóng vô tuyến. Vì vậy hãy gọi sớm trong chương trình, ngay sau robot.init(). Nút dừng của giao diện chỉ có tác dụng qua sóng vô tuyến nếu chương trình gọi robot.sleep().",
        },
      ],
    },
    wlan: {
      titel: "Wi-Fi",
      einleitung: "Với ROS 2, robot cần một mạng – mạng có sẵn hoặc mạng riêng.",
      einstellungen: [
        {
          name: "Kết nối với một mạng",
          vorgabe: "giới hạn thời gian 15 s",
          bereich: "chỉ WPA2-PSK",
          wirkung: "Tham gia một mạng có sẵn và trả về địa chỉ IP của robot.",
          hinweis:
            "Mạng đại học như eduroam không dùng được – vì thế mới có mạng riêng.",
        },
        {
          name: "Mở mạng riêng",
          vorgabe: "kênh 6",
          bereich: "mật khẩu ít nhất 8 ký tự, hoặc None cho mạng mở",
          wirkung: "Robot tạo một mạng Wi-Fi riêng; máy tính kết nối vào đó.",
        },
      ],
    },
    ros: {
      titel: "ROS 2",
      einleitung: "Đăng ký robot vào mạng ROS 2; cần Wi-Fi đang chạy.",
      einstellungen: [
        {
          name: "Đăng ký",
          vorgabe: "20 Hz, cổng 9001, cổng beacon 9000",
          bereich:
            "rate_hz 1 đến 50; tên gồm chữ thường, chữ số, dấu gạch dưới, không bắt đầu bằng chữ số",
          wirkung:
            "Tên trở thành namespace của các topic; rate_hz là tần số gửi giá trị đo.",
        },
        {
          name: "Thời hạn cho lệnh di chuyển",
          vorgabe: "0,5 s",
          wirkung: "Nếu không có /cmd_vel đến trong khoảng thời gian này, việc di chuyển được đặt về không.",
        },
        {
          name: "Thời hạn cho thiết bị bên kia",
          vorgabe: "5,0 s",
          wirkung: "Sau khoảng thời gian này không có thông điệp, máy tính im lặng sẽ bị quên.",
        },
      ],
    },
    sicherheit: {
      titel: "An toàn – không điều chỉnh được",
      einleitung:
        "Những giá trị này bảo vệ robot và con người và không thể thay đổi từ chương trình. Nên biết chúng, vì chúng giải thích vì sao động cơ không chạy hoặc robot đột nhiên dừng.",
      einstellungen: [],
      fest: [
        {
          name: "Dừng tại chỗ",
          wert: "nhấn cả hai nút cùng lúc",
          bedeutung:
            "Dừng ngay mọi động cơ và kết thúc các tác vụ đang chạy và bộ điều khiển riêng – trong lõi điều khiển, độc lập với chương trình, trình duyệt và kết nối vô tuyến. Chỉ thả nút thì không khởi động lại gì. Lối thoát khỏi mọi sự cố.",
        },
        {
          name: "Điện áp động cơ",
          wert: "tối đa 6,0 V",
          bedeutung:
            "Độ rộng xung được giới hạn theo điện áp ắc quy sao cho động cơ không bao giờ nhận quá 6 V. battery.duty_limit() hiển thị giới hạn hiện tại.",
        },
        {
          name: "Khóa động cơ khi không đo được ắc quy",
          wert: "dưới 4,0 V phép đo được coi là không hợp lệ",
          bedeutung:
            "Không có phép đo hợp lệ – không có ắc quy, chỉ có USB – động cơ vẫn bị khóa. Một giá trị không hợp lý không được dẫn đến độ rộng xung tối đa như khi ắc quy yếu.",
        },
        {
          name: "Ắc quy yếu",
          wert: "dưới 5,0 V",
          bedeutung: "Từ mức này battery.is_low() báo ắc quy yếu.",
        },
        {
          name: "Nhịp cơ bản của lõi điều khiển",
          wert: "2 ms, bước điều khiển mỗi 4 ms",
          bedeutung:
            "Lõi thứ hai của bộ xử lý điều khiển động cơ, đọc cảm biến và giám sát lệnh dừng tại chỗ theo nhịp này – kể cả khi chương trình bị treo.",
        },
        {
          name: "Siêu âm",
          wert: "2 cm đến 4 m, mỗi 60 ms",
          bedeutung:
            "Ngoài phạm vi này hoặc không có tiếng vọng, distance.read() trả về None – không bao giờ là một con số bịa ra. Một giá trị đo có hiệu lực tối đa 250 ms.",
        },
      ],
    },
  },
};

export default vi;
